import type { TravelStyle } from '../../../../types/wizard';
import { useTranslation } from 'react-i18next';
import StepCard from '../../../../components/wizard/StepCard';

interface Step2StyleProps {
  formData: {
    step2_style?: TravelStyle | '';
    step2_styles?: TravelStyle[];
  };
  updateFormData: (data: {
    step2_style?: TravelStyle | '';
    step2_styles?: TravelStyle[];
  }) => void;
  errors: Record<string, string>;
}

const MAX_SELECTED_STYLES = 3;

const Step2Style = ({ formData, updateFormData, errors }: Step2StyleProps) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const travelStyles: Array<{
    value: TravelStyle;
    label: string;
    description: string;
    icon: string;
  }> = [
    {
      value: 'Aventura',
      label: isEnglish ? 'Adventure' : 'Aventura',
      description: isEnglish ? 'Trails, adrenaline and high-energy experiences' : 'Trilhas, esportes radicais e experiências intensas',
      icon: 'ri-mountain-line',
    },
    {
      value: 'Luxo',
      label: isEnglish ? 'Luxury' : 'Luxo',
      description: isEnglish ? 'Premium experiences, comfort and exclusivity' : 'Experiências premium, conforto e exclusividade',
      icon: 'ri-vip-crown-line',
    },
    {
      value: 'Cultural',
      label: 'Cultural',
      description: isEnglish ? 'Museums, history, art and local immersion' : 'Museus, história, arte e imersão local',
      icon: 'ri-building-2-line',
    },
    {
      value: 'Gastronomia',
      label: isEnglish ? 'Food & Dining' : 'Gastronomia',
      description: isEnglish ? 'Authentic restaurants and culinary experiences' : 'Restaurantes autênticos e experiências culinárias',
      icon: 'ri-restaurant-line',
    },
    {
      value: 'Relaxamento',
      label: isEnglish ? 'Relaxation' : 'Relaxamento',
      description: isEnglish ? 'Beaches, spas and quiet moments' : 'Praias, spas e momentos de tranquilidade',
      icon: 'ri-sun-line',
    },
    {
      value: 'Família',
      label: isEnglish ? 'Family' : 'Família',
      description: isEnglish ? 'Activities for every age group' : 'Atividades para todas as idades',
      icon: 'ri-group-line',
    },
    {
      value: 'Lua de Mel',
      label: isEnglish ? 'Honeymoon' : 'Lua de Mel',
      description: isEnglish ? 'Romantic and intimate experiences' : 'Experiências românticas e intimistas',
      icon: 'ri-heart-line',
    },
    {
      value: 'Executivo',
      label: isEnglish ? 'Executive' : 'Executivo',
      description: isEnglish ? 'Efficiency, comfort and time optimization' : 'Eficiência, conforto e otimização de tempo',
      icon: 'ri-briefcase-4-line',
    },
    {
      value: 'Cruzeiro',
      label: isEnglish ? 'Cruise' : 'Cruzeiro',
      description: isEnglish ? 'Sailing and multiple destinations' : 'Navegação e múltiplos destinos',
      icon: 'ri-ship-line',
    },
  ];

  const selectedStyles =
    formData.step2_styles && formData.step2_styles.length > 0
      ? formData.step2_styles
      : formData.step2_style
      ? [formData.step2_style]
      : [];
  const getDisplayStyle = (styleValue: TravelStyle | '') =>
    travelStyles.find((style) => style.value === styleValue)?.label ?? styleValue;

  const handleToggleStyle = (styleValue: TravelStyle) => {
    const isSelected = selectedStyles.includes(styleValue);
    let nextStyles: TravelStyle[];

    if (isSelected) {
      nextStyles = selectedStyles.filter((style) => style !== styleValue);
    } else {
      if (selectedStyles.length >= MAX_SELECTED_STYLES) return;
      nextStyles = [...selectedStyles, styleValue];
    }

    updateFormData({
      step2_styles: nextStyles,
      step2_style: nextStyles[0] ?? '',
    });
  };

  return (
    <div className="space-y-8">
      <StepCard>
        <div className="mb-6">
          <label className="block text-white text-lg font-medium mb-2">
            {isEnglish ? 'Which travel style fits you best?' : 'Qual estilo de viagem combina mais com você?'} <span className="text-[#FFD700]">*</span>
          </label>
          <p className="text-white/90 text-sm mb-4">
            {isEnglish
              ? 'Choose up to 3 styles. The first one selected defines the primary profile of your itinerary.'
              : 'Escolha até 3 estilos. O primeiro selecionado guia o perfil principal do roteiro.'}
          </p>
        </div>

        <div className="mb-5 flex items-center justify-between gap-3 rounded-2xl border border-[#C9A84C]/20 bg-gradient-to-r from-[#C9A84C]/10 to-white/5 px-4 py-3">
          <span className="text-white font-medium text-sm">{isEnglish ? 'Selected styles' : 'Estilos selecionados'}</span>
          <div className="flex items-center gap-3">
            <span className="text-[#E8D4A0] font-bold text-sm">
              {selectedStyles.length}/{MAX_SELECTED_STYLES}
            </span>
            {selectedStyles.length > 0 && (
              <span className="hidden md:inline text-white/90 text-xs">
                {isEnglish ? 'Primary' : 'Principal'}: {getDisplayStyle(selectedStyles[0])}
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {travelStyles.map((style) => {
            const isSelected = selectedStyles.includes(style.value);
            const isPrimary = selectedStyles[0] === style.value;

            return (
              <button
                key={style.value}
                type="button"
                onClick={() => handleToggleStyle(style.value)}
                className={`p-5 rounded-xl border-2 transition-all duration-200 text-left ${
                  isSelected
                    ? 'border-[#FFD700] bg-[#FFD700]/10 shadow-[0_0_0_1px_rgba(255,215,0,0.08)]'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
              >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                    isSelected
                      ? 'bg-[#C9A84C]/20 border-[#C9A84C]/30 text-[#E8D4A0]'
                      : 'bg-white/10 border-white/10 text-white'
                  }`}
                >
                  <i className={`${style.icon} text-2xl`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-white font-semibold text-base">{style.label}</p>
                    {isSelected && (
                      <span className="inline-flex items-center rounded-full border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-2 py-0.5 text-[11px] font-bold text-[#E8D4A0]">
                        {isPrimary ? (isEnglish ? 'Primary' : 'Principal') : (isEnglish ? 'Secondary' : 'Complementar')}
                      </span>
                    )}
                  </div>
                  <p className="text-white/90 text-sm">{style.description}</p>
                </div>
                {isSelected && (
                  <i className="ri-checkbox-circle-fill text-[#E8D4A0] text-xl flex-shrink-0"></i>
                )}
              </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {selectedStyles.map((style, index) => (
            <span
              key={style}
              className="inline-flex items-center gap-2 rounded-full border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-1.5 text-xs font-semibold text-white"
            >
              <span className="text-[#E8D4A0]">{index === 0 ? (isEnglish ? 'Primary' : 'Principal') : 'Extra'}</span>
              <span>{getDisplayStyle(style)}</span>
            </span>
          ))}
        </div>

        {(errors.step2_style || errors.travelStyle) && (
          <p className="text-red-400 text-sm mt-4 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {errors.step2_style || errors.travelStyle}
          </p>
        )}
      </StepCard>
    </div>
  );
};

export default Step2Style;
